import { io } from 'socket.io-client';
import useAuthStore from '../stores/authStore';
import authApi from './authApi';

// In production (when VITE_SOCKET_URL is empty), use current origin (handled by nginx proxy)
// In development, use localhost:4001
const SOCKET_URL = import.meta.env.VITE_SOCKET_URL ||
  (import.meta.env.MODE === 'production' ? window.location.origin : 'http://localhost:4001');

class SocketService {
  constructor() {
    this.socket = null;
    this.connected = false;
    this.activeSessionId = null; // Track active session for reconnection
    this._reconnectListeners = []; // Listeners for reconnection events
  }

  connect() {
    if (this.socket) {
      return this.socket;
    }

    // Get access token from auth store
    const accessToken = useAuthStore.getState().accessToken;

    if (!accessToken) {
      console.error('Cannot connect socket: No access token');
      return null;
    }

    this.socket = io(SOCKET_URL, {
      // Read the current token at each handshake, including reconnects after
      // the HTTP client has refreshed the original 15-minute access token.
      auth: callback => callback({ token: useAuthStore.getState().accessToken }),
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    const socket = this.socket;
    let attemptedRefresh = false;
    socket.on('connect', () => {
      attemptedRefresh = false;
      console.log('Socket connected');
      const wasConnected = this.connected;
      this.connected = true;

      // If this is a reconnection and we had an active session, try to recover it
      if (wasConnected === false && this.activeSessionId) {
        console.log(`Reconnected - attempting to recover session ${this.activeSessionId}`);
        this.socket.emit('session:reconnect', { sessionId: this.activeSessionId });
      }
    });

    socket.on('connect_error', async (error) => {
      console.error('Socket connection error:', error.message);
      if (error.message !== 'Invalid or expired token' && error.message !== 'Authentication required') return;
      if (attemptedRefresh) {
        useAuthStore.getState().logout();
        window.location.href = '/login';
        return;
      }
      attemptedRefresh = true;
      try {
        const result = await authApi.refreshAccessToken();
        if (this.socket !== socket) return;
        useAuthStore.getState().setAccessToken(result.data.accessToken);
        socket.connect();
      } catch (refreshError) {
        if (this.socket !== socket) return;
        useAuthStore.getState().logout();
        window.location.href = '/login';
      }
    });

    this.socket.on('disconnect', (reason) => {
      console.log('Socket disconnected, reason:', reason);
      this.connected = false;
    });

    this.socket.on('error', (error) => {
      console.error('Socket error:', error);

      // If authentication error, logout user
      if (error.message === 'Invalid or expired token' || error.message === 'Authentication required') {
        useAuthStore.getState().logout();
        window.location.href = '/login';
      }
    });

    return this.socket;
  }

  setActiveSession(sessionId) {
    this.activeSessionId = sessionId;
  }

  clearActiveSession() {
    this.activeSessionId = null;
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.connected = false;
    }
  }

  emit(event, data) {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }
    this.socket.emit(event, data);
  }

  on(event, callback) {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }
    this.socket.on(event, callback);
  }

  off(event, callback) {
    if (!this.socket) {
      return;
    }
    this.socket.off(event, callback);
  }

  isConnected() {
    return this.connected;
  }
}

export default new SocketService();
